import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getAdminOrderForensics,
  type AdminOrderForensicsResponse,
  type WireOrderForensics,
} from '../api/endpoints.js';
import { useApiQuery, useVisiblePolling } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IdMono,
  Input,
  LoadingState,
  PageHead,
  StatusBadge,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';

/**
 * Order detail. The page answers ONE question first — "why did no mail arrive?"
 * — and only then shows the raw record.
 *
 * The old version led with SKU and provider id, neither of which an operator
 * acts on, and stopped short of the two facts that actually explain a missing
 * mail: which mailbox was rented, and whether that mailbox's PARENT account can
 * log in at all. Those now sit at the top as a rental chain, with the server's
 * verdict above them.
 */

/** micro-USD → "$0.0020" (four places; sub-cent prices are the normal case). */
function microUsd(micro: number): string {
  return `$${(micro / 1_000_000).toFixed(4)}`;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR');
  } catch {
    return iso;
  }
}

/** "3 dk önce" / "12 dk sonra" — the age of a timestamp relative to now. */
function relative(iso: string | null | undefined): string {
  if (!iso) return 'kayıt yok';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'kayıt yok';
  const deltaMin = Math.round((t - Date.now()) / 60000);
  const abs = Math.abs(deltaMin);
  const unit = abs < 60 ? `${abs} dk` : abs < 1440 ? `${Math.round(abs / 60)} sa` : `${Math.round(abs / 1440)} gün`;
  if (abs < 1) return 'az önce';
  return deltaMin < 0 ? `${unit} önce` : `${unit} sonra`;
}

export function OrderInspectPage(): JSX.Element {
  const { orderId: routeId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(routeId ?? '');
  const orderId = routeId ?? '';
  const canInspect = useCan('order:inspect');

  const query = useApiQuery<AdminOrderForensicsResponse>(
    (options) => getAdminOrderForensics(options, orderId),
    [orderId],
  );
  // An open order changes minute to minute (lease countdown, arriving mail).
  useVisiblePolling(query.reload, 10000, canInspect && orderId.length > 0);

  function onSubmit(ev: FormEvent): void {
    ev.preventDefault();
    const trimmed = search.trim();
    if (!trimmed) return;
    navigate(`/orders/${encodeURIComponent(trimmed)}`);
  }

  return (
    <div>
      <PageHead
        crumb={
          orderId ? (
            <>
              <Link to="/orders" style={{ color: 'var(--text-tertiary)' }}>Siparişler</Link>
              <Icon.ChevronRight size={11} /> Detay
            </>
          ) : (
            <>Yönetim <Icon.ChevronRight size={11} /> Siparişler</>
          )
        }
        title={orderId ? 'Sipariş Detayı' : 'Siparişler'}
        subtitle="Siparişin arkasındaki kiralama zinciri: hangi site için satıldı, hangi kutu kiralandı, o kutunun parent hesabı sağlıklı mı. YALNIZCA OPERATÖR: parent hesap envanterimizdir, müşteri yüzeyine hiçbir zaman çıkmaz (müşteri sadece kiraladığı kutuyu görür). Kimlik bilgileri ve mail gövdesi bu ekrana da gelmez."
        actions={
          orderId ? (
            <Button variant="secondary" size="sm" icon={<Icon.Refresh size={14} />} onClick={query.reload}>
              Yenile
            </Button>
          ) : undefined
        }
      />

      <Card>
        <form onSubmit={onSubmit} aria-label="sipariş arama">
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Input
                type="text"
                placeholder="sipariş id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="sipariş id"
                iconLeft={<Icon.Search size={14} />}
              />
            </div>
            <Button type="submit" variant="primary" icon={<Icon.ArrowUR size={14} />}>
              Aç
            </Button>
          </div>
        </form>
      </Card>

      {!orderId ? (
        <EmptyState
          label="Yukarıya bir sipariş id girin."
          hint="Müşteri detay sayfasındaki sipariş listesinden kopyalayabilirsiniz."
          icon={<Icon.Inbox size={22} />}
        />
      ) : null}
      {orderId && !canInspect ? <ErrorState reason="capability_missing:order:inspect" /> : null}
      {orderId && canInspect && query.kind === 'loading' ? <LoadingState /> : null}
      {orderId && canInspect && query.kind === 'error' ? (
        <ErrorState reason={query.reason} onRetry={query.reload} />
      ) : null}
      {orderId && canInspect && query.kind === 'ok' ? (
        <OrderDetail data={query.data.data} correlationId={query.data.correlationId} />
      ) : null}
    </div>
  );
}

function OrderDetail({
  data,
  correlationId,
}: {
  readonly data: WireOrderForensics;
  readonly correlationId: string;
}): JSX.Element {
  const { order, reservation, mailbox, parent, charge, messages, timeline, polling, diagnosis } = data;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Verdict diagnosis={diagnosis} />
      <Chain order={order} mailbox={mailbox} parent={parent} />

      <div className="fact-grid">
        <Card flush>
          <CardHeader title="Sipariş" actions={<StatusBadge status={order.state} />} />
          <CardBody>
            <div className="dl-rows">
              <Row k="Sipariş id" v={<IdMono value={order.orderId} />} />
              <Row
                k="Site"
                v={order.siteLabel ? <strong>{order.siteLabel}</strong> : <span className="t-tertiary">—</span>}
              />
              <Row k="Mail alan adı" v={<span className="t-mono">{order.domain ?? '—'}</span>} />
              <Row
                k="Müşteri"
                v={
                  order.actorId ? (
                    <Link className="kop-link" to={`/users/${encodeURIComponent(order.actorId)}`}>
                      {order.actorEmail ?? order.actorId}
                    </Link>
                  ) : (
                    '—'
                  )
                }
              />
              <Row k="Açılış" v={<Stamp iso={order.openedAt} />} />
              <Row k="Son güncelleme" v={<Stamp iso={order.updatedAt} />} />
              <Row k="Kapanış" v={order.closedAt ? <Stamp iso={order.closedAt} /> : <span className="t-tertiary">açık</span>} />
              <Row k="Correlation" v={<IdMono value={order.correlationId ?? correlationId} />} />
            </div>
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title="Kiralama penceresi"
            actions={
              reservation ? (
                <Badge tone={reservation.leaseActive ? 'success' : 'neutral'} dot>
                  {reservation.leaseActive ? 'açık' : 'kapandı'}
                </Badge>
              ) : undefined
            }
          />
          <CardBody>
            {reservation ? (
              <div className="dl-rows">
                <Row k="Sağlayıcı" v={<ProviderChip providerId={reservation.providerId} />} />
                <Row k="Rezervasyon" v={<IdMono value={reservation.reservationId} />} />
                <Row k="Durum" v={<span className="t-mono">{reservation.status ?? '—'}</span>} />
                <Row
                  k="Süre sonu"
                  v={
                    reservation.leaseExpiresAt ? (
                      <span>
                        <span className="t-mono t-xs">{fmt(reservation.leaseExpiresAt)}</span>{' '}
                        <span className={reservation.leaseActive ? 't-xs' : 't-xs t-tertiary'}>
                          ({relative(reservation.leaseExpiresAt)})
                        </span>
                      </span>
                    ) : (
                      '—'
                    )
                  }
                />
                <Row k="SKU" v={<span className="t-mono t-xs t-tertiary">{reservation.sku ?? '—'}</span>} />
              </div>
            ) : (
              <EmptyState label="Bu siparişe hiç rezervasyon bağlanmamış." hint="Stok bulunamadan kapanmış olabilir." />
            )}
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title="Ücret"
            actions={
              <span className="t-mono t-num" style={{ fontSize: 17, fontWeight: 600 }}>
                {microUsd(charge.netMicro)}
              </span>
            }
          />
          <CardBody>
            <div className="dl-rows">
              <Row k="Tahsil" v={<span className="t-mono t-num">{microUsd(charge.chargedMicro)}</span>} />
              <Row
                k="İade"
                v={
                  charge.refundedMicro > 0 ? (
                    <span className="t-mono t-num">−{microUsd(charge.refundedMicro)}</span>
                  ) : (
                    <span className="t-tertiary">yok</span>
                  )
                }
              />
              <Row k="Tahsil zamanı" v={<Stamp iso={charge.chargedAt} />} />
              {charge.refundedMicro > 0 ? <Row k="İade zamanı" v={<Stamp iso={charge.refundedAt} />} /> : null}
            </div>
            <div className="t-xs t-tertiary" style={{ marginTop: 10 }}>
              Tutarlar ledger'daki micro değerden okunur; kuruşa yuvarlanmış görünüm değil.
            </div>
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title="Kiralanan kutu"
            actions={mailbox ? <Badge tone={aliasTone(mailbox.status)} dot>{mailbox.status ?? '—'}</Badge> : undefined}
          />
          <CardBody>
            {mailbox ? (
              <div className="dl-rows">
                <Row k="Adres" v={<CopyText value={mailbox.address ?? '—'} />} />
                <Row k="Alan adı" v={<span className="t-mono">{mailbox.domain ?? '—'}</span>} />
                <Row k="Kaç kez satıldı" v={<span className="t-mono t-num">{mailbox.servedCount}</span>} />
                <Row k="Son satış" v={<Stamp iso={mailbox.lastServedAt} />} />
                <Row k="Oluşturma" v={<Stamp iso={mailbox.createdAt} />} />
                <Row k="Alias id" v={<IdMono value={mailbox.aliasId} />} />
              </div>
            ) : (
              <EmptyState label="Kutu çözümlenemedi." hint="Rezervasyona bir alias atanmamış." />
            )}
          </CardBody>
        </Card>

        <Card flush>
          <CardHeader
            title="Parent hesap"
            actions={parent ? <Badge tone={parentTone(parent.status)} dot>{parent.status ?? '—'}</Badge> : undefined}
          />
          <CardBody>
            {parent ? (
              <>
                <div className="dl-rows">
                  <Row k="Hesap" v={<CopyText value={parent.handle ?? '—'} />} />
                  <Row k="Oturum" v={<span className="t-mono">{parent.sessionState ?? '—'}</span>} />
                  <Row
                    k="Üst üste hata"
                    v={
                      <span className={parent.consecutiveErrors > 0 ? 't-mono t-num t-danger' : 't-mono t-num'}>
                        {parent.consecutiveErrors}
                      </span>
                    }
                  />
                  <Row
                    k="Son başarılı okuma"
                    v={
                      parent.lastSuccessfulFetchAt ? (
                        <span>
                          <span className="t-mono t-xs">{fmt(parent.lastSuccessfulFetchAt)}</span>{' '}
                          <span className="t-xs t-tertiary">({relative(parent.lastSuccessfulFetchAt)})</span>
                        </span>
                      ) : (
                        // No inbox worker writes this column yet; NULL is
                        // "not tracked", not evidence of a dead inbox.
                        <span className="t-tertiary">kaydedilmiyor</span>
                      )
                    }
                  />
                  <Row k="Son oturum kontrolü" v={<Stamp iso={parent.lastSessionCheckedAt} />} />
                  <Row
                    k="Cooldown"
                    v={parent.cooldownUntil ? <Stamp iso={parent.cooldownUntil} /> : <span className="t-tertiary">yok</span>}
                  />
                  {parent.suspendedReason ? <Row k="Askı nedeni" v={parent.suspendedReason} /> : null}
                </div>
                {reservation?.providerId ? (
                  <div style={{ marginTop: 12 }}>
                    <Link
                      className="kop-link"
                      to={`/providers/${encodeURIComponent(reservation.providerId)}?q=${encodeURIComponent(parent.handle ?? '')}`}
                    >
                      Sağlayıcı sayfasında aç →
                    </Link>
                  </div>
                ) : null}
              </>
            ) : (
              <EmptyState label="Parent hesap çözümlenemedi." />
            )}
          </CardBody>
        </Card>
      </div>

      <Card flush>
        <CardHeader
          title="Gelen mailler"
          actions={
            <Badge tone={messages.length > 0 ? 'success' : 'neutral'} dot>
              {messages.length > 0 ? `${messages.length} mail` : 'hiç mail yok'}
            </Badge>
          }
        />
        <CardBody tight style={{ padding: messages.length === 0 ? 20 : 0 }}>
          {messages.length === 0 ? (
            <EmptyState
              label="Bu siparişin kutusuna hiç mail düşmedi."
              hint={
                polling
                  ? `Havuz durumu: ${polling.status} (${fmt(polling.updatedAt)})`
                  : 'Kutu dinlendiyse mail hiç gelmemiş demektir; yukarıdaki teşhise bakın.'
              }
              icon={<Icon.Inbox size={22} />}
            />
          ) : (
            <Table dense>
              <THead>
                <TRow>
                  <TH>Zaman</TH>
                  <TH>Gönderen</TH>
                  <TH>Konu</TH>
                  <TH>Kod</TH>
                </TRow>
              </THead>
              <TBody>
                {messages.map((m) => (
                  <TRow key={m.id}>
                    <TD className="t-mono t-xs t-tertiary">{fmt(m.receivedAt)}</TD>
                    <TD className="t-mono t-xs">{m.from ?? '—'}</TD>
                    <TD style={{ wordBreak: 'break-word' }}>{m.subject ?? '—'}</TD>
                    <TD>
                      {m.hasCode ? (
                        <Badge tone="success" dot>
                          okundu{m.codeSource ? ` (${m.codeSource})` : ''}
                        </Badge>
                      ) : (
                        <Badge tone="warn" dot>okunamadı</Badge>
                      )}
                    </TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Card flush>
        <CardHeader
          title="Teslimat olayları"
          actions={<span className="t-xs t-tertiary">{timeline.length} kayıt</span>}
        />
        <CardBody tight style={{ padding: timeline.length === 0 ? 20 : 0 }}>
          {timeline.length === 0 ? (
            <EmptyState label="Teslimat olayı yok." hint="Sipariş hiç terminal bir sonuca ulaşmamış." />
          ) : (
            <Table dense>
              <THead>
                <TRow>
                  <TH>Zaman</TH>
                  <TH>Sonuç</TH>
                  <TH>Sağlayıcı</TH>
                  <TH>Konu / hata</TH>
                  <TH>Gönderen</TH>
                </TRow>
              </THead>
              <TBody>
                {timeline.map((e) => (
                  <TRow key={e.eventId}>
                    <TD className="t-mono t-xs t-tertiary">{fmt(e.recordedAt)}</TD>
                    <TD><OutcomeBadge kind={e.outcomeKind} /></TD>
                    <TD className="t-mono t-xs">{e.providerId}</TD>
                    <TD className="t-xs" style={{ wordBreak: 'break-word' }}>
                      <TimelineText subject={e.subject} failureReason={e.failureReason} detail={e.detail} />
                    </TD>
                    <TD className="t-mono t-xs">{e.from ?? '—'}</TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/** The headline: the server's one-line answer to "why did no mail arrive?". */
function Verdict({ diagnosis }: { readonly diagnosis: WireOrderForensics['diagnosis'] }): JSX.Element {
  const icon =
    diagnosis.severity === 'ok' ? <Icon.CheckCircle size={18} />
    : diagnosis.severity === 'critical' ? <Icon.Warn size={18} />
    : diagnosis.severity === 'warn' ? <Icon.Warn size={18} />
    : <Icon.Info size={18} />;
  return (
    <div className={`ord-verdict ord-verdict--${diagnosis.severity}`} data-testid="order-verdict">
      <span className="ord-verdict-icon" aria-hidden="true">{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div className="ord-verdict-title">{diagnosis.title}</div>
        <div className="ord-verdict-detail">{diagnosis.detail}</div>
      </div>
    </div>
  );
}

/**
 * The rental chain, left to right: the site the mail was bought for, the
 * mailbox that served it, the parent whose inbox is polled. Read it as a
 * pipeline — the first red tile is where delivery stopped.
 */
function Chain({
  order,
  mailbox,
  parent,
}: {
  readonly order: WireOrderForensics['order'];
  readonly mailbox: WireOrderForensics['mailbox'];
  readonly parent: WireOrderForensics['parent'];
}): JSX.Element {
  return (
    <div className="ord-chain" data-testid="order-chain">
      <ChainStep
        label="Site"
        value={order.siteLabel ?? '—'}
        note={order.domain ? `${order.domain} kutusu` : null}
        tone="neutral"
      />
      <ChainArrow />
      <ChainStep
        label="Kiralanan kutu"
        value={mailbox?.address ?? 'çözümlenemedi'}
        note={mailbox ? `durum: ${mailbox.status ?? '—'}` : null}
        tone={mailbox ? aliasTone(mailbox.status) : 'danger'}
      />
      <ChainArrow />
      <ChainStep
        label="Parent hesap"
        value={parent?.handle ?? 'çözümlenemedi'}
        note={
          parent
            ? `${parent.status ?? '—'}${parent.consecutiveErrors > 0 ? ` · ${parent.consecutiveErrors} hata` : ''}`
            : null
        }
        tone={parent ? parentTone(parent.status) : 'danger'}
      />
    </div>
  );
}

function ChainStep({
  label,
  value,
  note,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string | null;
  readonly tone: 'success' | 'warn' | 'danger' | 'neutral';
}): JSX.Element {
  return (
    <div className={`ord-chain-step ord-chain-step--${tone}`}>
      <div className="ord-chain-label">{label}</div>
      <div className="ord-chain-value" title={value}>{value}</div>
      {note ? <div className="ord-chain-note">{note}</div> : null}
    </div>
  );
}

function ChainArrow(): JSX.Element {
  return (
    <div className="ord-chain-arrow" aria-hidden="true">
      <Icon.ChevronRight size={16} />
    </div>
  );
}

/** A mailbox is fine while it is sellable or rented; anything else is a flag. */
function aliasTone(status: string | null): 'success' | 'warn' | 'danger' | 'neutral' {
  switch (status) {
    case 'available':
    case 'leased':
    case 'active':
      return 'success';
    case 'used':
    case 'released':
      return 'neutral';
    case 'deleted':
      return 'neutral';
    case 'quarantined':
    case 'blocked':
      return 'danger';
    default:
      return 'warn';
  }
}

/** Anything other than a polling-capable parent stops delivery outright. */
function parentTone(status: string | null): 'success' | 'warn' | 'danger' | 'neutral' {
  switch (status) {
    case 'active':
    case 'idle':
    case 'busy':
    case 'ready':
      return 'success';
    case 'cooldown':
      return 'warn';
    case null:
      return 'neutral';
    default:
      return 'danger';
  }
}

function ProviderChip({ providerId }: { readonly providerId: string | null }): JSX.Element {
  if (!providerId) return <span className="t-tertiary">—</span>;
  return (
    <Link className="kop-link t-mono" to={`/providers/${encodeURIComponent(providerId)}`}>
      {providerId}
    </Link>
  );
}

function Stamp({ iso }: { readonly iso: string | null }): JSX.Element {
  if (!iso) return <span className="t-tertiary">—</span>;
  return (
    <span>
      <span className="t-mono t-xs">{fmt(iso)}</span>{' '}
      <span className="t-xs t-tertiary">({relative(iso)})</span>
    </span>
  );
}

/** A selectable, wrapping mail address — long addresses must not clip. */
function CopyText({ value }: { readonly value: string }): JSX.Element {
  return (
    <span className="t-mono" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
      {value}
    </span>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}

/**
 * "Konu / hata" cell of a delivery event. A mail row shows its subject, as
 * before. A failure row shows the reason AND, on a second muted line, the
 * event's `detail` — for a `timeout_no_message` row that is the evidence the
 * lease-expiry sweeper recorded (customer read, inbox listing, parent state),
 * which the old `subject ?? failureReason ?? detail` chain always hid.
 */
function TimelineText({
  subject,
  failureReason,
  detail,
}: {
  readonly subject: string | null;
  readonly failureReason: string | null;
  readonly detail: string | null;
}): JSX.Element {
  if (subject) return <>{subject}</>;
  const extra = detail && detail !== failureReason ? detail : null;
  if (!failureReason) return <>{extra ?? '—'}</>;
  return (
    <>
      <div className="t-mono">{failureReason}</div>
      {extra ? (
        <div className="t-tertiary" data-testid="timeline-detail">
          {extra}
        </div>
      ) : null}
    </>
  );
}

function OutcomeBadge({ kind }: { readonly kind: string }): JSX.Element {
  switch (kind) {
    case 'delivered':      return <Badge tone="success" dot>{kind}</Badge>;
    case 'failed':         return <Badge tone="danger" dot>{kind}</Badge>;
    case 'pending_review': return <Badge tone="warn" dot>{kind}</Badge>;
    default:               return <Badge tone="neutral" dot>{kind}</Badge>;
  }
}
